import type { ServiceTag, ServiceTagColor } from "../types";

export const SERVICE_TAG_COLORS: ServiceTagColor[] = [
	"slate",
	"gray",
	"blue",
	"cyan",
	"teal",
	"green",
	"yellow",
	"amber",
	"orange",
	"red",
	"pink",
	"purple",
	"indigo",
];

const TAG_COLOR_CLASSES: Record<
	ServiceTagColor,
	{ bg: string; text: string; border: string }
> = {
	slate: {
		bg: "bg-slate-50 dark:bg-slate-900/50",
		text: "text-slate-800 dark:text-slate-300",
		border: "border-slate-200 dark:border-slate-700",
	},
	gray: {
		bg: "bg-secondary",
		text: "text-muted-foreground",
		border: "border-border",
	},
	blue: {
		bg: "bg-accent",
		text: "text-accent-foreground",
		border: "border-ring/30",
	},
	cyan: {
		bg: "bg-cyan-50 dark:bg-cyan-950/40",
		text: "text-cyan-800 dark:text-cyan-300",
		border: "border-cyan-200 dark:border-cyan-900/60",
	},
	teal: {
		bg: "bg-teal-50 dark:bg-teal-950/40",
		text: "text-teal-800 dark:text-teal-300",
		border: "border-teal-200 dark:border-teal-900/60",
	},
	green: {
		bg: "bg-green-50 dark:bg-green-950/40",
		text: "text-green-800 dark:text-green-300",
		border: "border-green-200 dark:border-green-900/60",
	},
	yellow: {
		bg: "bg-yellow-50 dark:bg-yellow-950/40",
		text: "text-yellow-800 dark:text-yellow-300",
		border: "border-yellow-200 dark:border-yellow-900/60",
	},
	amber: {
		bg: "bg-amber-50 dark:bg-amber-950/40",
		text: "text-amber-800 dark:text-amber-300",
		border: "border-amber-200 dark:border-amber-900/60",
	},
	orange: {
		bg: "bg-orange-50 dark:bg-orange-950/40",
		text: "text-orange-800 dark:text-orange-300",
		border: "border-orange-200 dark:border-orange-900/60",
	},
	red: {
		bg: "bg-red-50 dark:bg-red-950/40",
		text: "text-red-800 dark:text-red-300",
		border: "border-red-200 dark:border-red-900/60",
	},
	pink: {
		bg: "bg-pink-50 dark:bg-pink-950/40",
		text: "text-pink-800 dark:text-pink-300",
		border: "border-pink-200 dark:border-pink-900/60",
	},
	purple: {
		bg: "bg-purple-50 dark:bg-purple-950/40",
		text: "text-purple-800 dark:text-purple-300",
		border: "border-purple-200 dark:border-purple-900/60",
	},
	indigo: {
		bg: "bg-indigo-50 dark:bg-indigo-950/40",
		text: "text-indigo-800 dark:text-indigo-300",
		border: "border-indigo-200 dark:border-indigo-900/60",
	},
};

export function getServiceTagClassName(
	color: ServiceTagColor,
	options: { deleted?: boolean } = {},
): string {
	const colorClasses = TAG_COLOR_CLASSES[color] ?? TAG_COLOR_CLASSES.blue;
	return [
		"border",
		colorClasses.border,
		colorClasses.bg,
		colorClasses.text,
		options.deleted ? "opacity-60" : "",
	].join(" ");
}

export function isTagDeleted(tag: ServiceTag): boolean {
	return Boolean(tag.deletedAt);
}

export function getVisibleServiceTags(
	tags: ServiceTag[] | undefined,
): ServiceTag[] {
	return (tags || []).filter((tag) => !isTagDeleted(tag));
}

function buildServiceTagReferenceMap(
	serviceTags: ServiceTag[],
): Map<string, ServiceTag> {
	const references = new Map<string, ServiceTag>();
	for (const tag of serviceTags) {
		references.set(tag.id, tag);
		references.set(tag.label, tag);
	}
	return references;
}

export function hasServiceTagReference(
	tagRefs: string[] | undefined,
	tag: ServiceTag,
): boolean {
	if (!tagRefs || tagRefs.length === 0) {
		return false;
	}
	return tagRefs.some((tagRef) => tagRef === tag.id || tagRef === tag.label);
}

export function resolveTagIds(
	tagIds: string[] | undefined,
	serviceTags: ServiceTag[] | undefined,
): ServiceTag[] {
	if (
		!tagIds ||
		tagIds.length === 0 ||
		!serviceTags ||
		serviceTags.length === 0
	) {
		return [];
	}
	const tagMap = buildServiceTagReferenceMap(serviceTags);
	const seenTagIds = new Set<string>();
	return tagIds
		.map((tagId) => tagMap.get(tagId))
		.filter((tag): tag is ServiceTag => {
			if (!tag || seenTagIds.has(tag.id)) {
				return false;
			}
			seenTagIds.add(tag.id);
			return true;
		});
}

export function normalizeTagIds(
	tagRefs: string[] | undefined,
	serviceTags: ServiceTag[] | undefined,
): string[] {
	if (!tagRefs || tagRefs.length === 0) {
		return [];
	}
	if (!serviceTags || serviceTags.length === 0) {
		return [...new Set(tagRefs)];
	}
	const tagMap = buildServiceTagReferenceMap(serviceTags);
	const normalized: string[] = [];
	const seen = new Set<string>();

	for (const tagRef of tagRefs) {
		const canonicalRef = tagMap.get(tagRef)?.id ?? tagRef;
		if (!seen.has(canonicalRef)) {
			seen.add(canonicalRef);
			normalized.push(canonicalRef);
		}
	}

	return normalized;
}
